import React, { useEffect, useState } from "react";
import "../styles/CreateRequisition.scss";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import { Country, State, City } from "country-state-city";
import {
  getAllDepartments,
  getAllJobTypes,
  getAllSkills,
  getRequisition,
  getRecruiterMappings,
  getActiveClients,
} from "../services/api";

function EditRequisition() {
  const { id: requisitionId } = useParams();
  const [form, setForm] = useState({
    job_id: "",
    job_title: "",
    departments: [],
    client_id: "",
    client_name: "",
    client_website: "",
    min_budget: "",
    max_budget: "",
    workmode: "",
    job_type: [],
    openings: "",
    locations: "",
    notice_period_days: "",
    job_desc: "",
    skills: [],
    skillInput: "",
    years_of_exp: "",
    remarks: "",
  });

  const [error, setError] = useState("");
  const [skillOptions, setSkillOptions] = useState([]);
  const [assignedRecruiters, setAssignedRecruiters] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [jobTypeOptions, setJobTypeOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const navigate = useNavigate();

  // 🔄 Fetch requisition details
  useEffect(() => {
    (async () => {
      try {
        // 🔄 Show loading spinner
        Swal.fire({
          html: `<img src=${require("../assets/loader.gif")} style="width: 50px" alt="loader">`,
          showConfirmButton: false,
          heightAuto: false,
          customClass: { popup: "loader-swal" },
          allowOutsideClick: false,
        });

        // 📡 API Call
        const res = await getRequisition(requisitionId);
        Swal.close();

        // ✅ On Success
        if (res?.result_code === 200 && res?.result_info) {
          const r = res.result_info;
          const client = res.client || {};

          setForm({
            job_id: r.job_id || "",
            job_title: r.job_title || "",
            departments: Array.isArray(r.department_ids)
              ? r.department_ids.map(String)
              : r.departments
              ? String(r.departments)
                  .split(",")
                  .map((s) => s.trim())
              : [],
            client_id: client.id ? String(client.id) : "",
            client_name: client.name || "",
            client_website: client.website || "",
            min_budget: r.min_budget ? String(r.min_budget) : "",
            workmode: r.workmode || "",
            max_budget: r.max_budget ? String(r.max_budget) : "",
            job_type: Array.isArray(r.job_type_ids)
              ? r.job_type_ids.map(String)
              : r.job_type
              ? String(r.job_type)
                  .split(",")
                  .map((s) => s.trim())
              : [],
            openings:
              r.openings !== undefined && r.openings !== null
                ? String(r.openings)
                : "",
            locations: r.locations ? String(r.locations).split(",").
                                      map((s) => s.trim()): [],
            notice_period_days:
                r.notice_period_days !== undefined && r.notice_period_days !== null
                ? String(r.notice_period_days): "",
            job_desc: r.job_desc || "",
            skills: r.skills ? r.skills.split(/, ?/) : [],
            skillInput: "",
            years_of_exp:
              r.years_of_exp !== undefined && r.years_of_exp !== null
                ? String(r.years_of_exp)
                : "",
            remarks: r.remarks || "",
          });

          const formRecruiters =
            r.recruiter_details && Array.isArray(r.recruiter_details)
              ? r.recruiter_details
              : [];
          let allRecruiters = [...formRecruiters];

          try {
            const mappingRes = await getRecruiterMappings(requisitionId);
            if (
              mappingRes?.result_code === 200 &&
              mappingRes?.result_info?.recruiter_details
            ) {
              allRecruiters.push(...mappingRes.result_info.recruiter_details);
            }
          } catch (fetchError) {
            console.error("Error fetching recruiter mappings:", fetchError);
          }

          const uniqueRecruiters = Array.from(
            new Map(
              allRecruiters.map((rec) => {
                const key = rec.id
                  ? `id_${rec.id}`
                  : `name_${String(rec.name || rec.email || rec).trim()}`;
                return [key, rec];
              })
            ).values()
          );
          setAssignedRecruiters(uniqueRecruiters);
        } else {
          // ⚠️ If response structure is unexpected or error
          Swal.fire({
            icon: "error",
            title: "Failed to load requisition",
            text: res?.error_info || "Unable to fetch requisition details.",
            confirmButtonColor: "#d33",
          });
          setError("Failed to fetch requisition details.");
        }
      } catch (error) {
        Swal.close();
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Something went wrong while fetching requisition details.",
          confirmButtonColor: "#d33",
        });
        setError("Failed to fetch requisition details.");
      } finally {
        // setLoading(false);
      }
    })();
  }, [requisitionId]);

  const handleSkillsChange = (selected) => {
    setForm((f) => ({
      ...f,
      skills: selected ? selected.map((opt) => opt.value) : [],
    }));
  };

  const handleJobTypeChange = (selected) => {
    setForm((f) => ({
      ...f,
      job_type: selected ? selected.map((opt) => opt.value) : [],
    }));
  };

  const handleLocationsChange = (selected) => {
    setForm((f) => ({
      ...f,
      locations: selected ?selected.map((item) => item.value): [],
    }));
  };

  const handleDepartmentChange = (selected) => {
    setForm((f) => ({
      ...f,
      departments: selected ? selected.map((opt) => opt.value) : [],
    }));
  };

  // For Departments, Job Types, Skills, Locations, Recruiters
  const fetchSkills = async (query) => {
    try {
      const response = await getAllSkills();
      if (
        response &&
        response.result_code === 200 &&
        Array.isArray(response.result_info)
      ) {
        const allSkills = response.result_info.map(
          (skill) => skill.skill_name || skill.name || skill
        );
        if (!query) return allSkills;
        return allSkills.filter((skill) =>
          skill.toLowerCase().includes(query.toLowerCase())
        );
      }
      return [];
    } catch (error) {
      console.error("Error fetching skills:", error);
      return [];
    }
  };

  // Function to get Indian cities
  const getIndianCities = () => {
    try {
      // Get India country code
      const india = Country.getCountryByCode("IN");
      if (!india) return [];

      // Get all states of India
      const indianStates = State.getStatesOfCountry("IN");
      if (!indianStates || indianStates.length === 0) return [];

      // Get cities from all states
          const extraCities = ["Bhuwaneshwar"]; 
           const removeCities = ["Bangalore Urban", "Bangalore Rural", "Ghandhi Nagar","Lucknow District"];
     
     
            const removeSet = new Set(
                [...extraCities, ...removeCities].map(city => city.toLowerCase())
                   );
     
                 const existingCities = new Set();
                  let allCities = [];
     
                  indianStates.forEach((state) => {
                  const cities = City.getCitiesOfState("IN", state.isoCode);
     
                   if (!cities || cities.length === 0) return;
     
                  cities.forEach((city) => {
                  const name = city?.name?.trim();
                   const key = name?.toLowerCase();
     
                    if (
                        name &&
                        key &&
                        !existingCities.has(key) &&
                        !removeSet.has(key)
                          ) {
                        existingCities.add(key);
     
                        allCities.push({
                        value: name,
                        label: name,
                           });
                        }
                      });
                   });
     
     
                 allCities.push({
                     value: "Remote",
                     label: "Remote",
                          });
     
    
                  allCities.sort((a, b) => a.label.localeCompare(b.label));
     
                return allCities;
    } catch (error) {
      console.error("Error fetching Indian cities:", error);
      return [];
    }
  };

  useEffect(() => {
    let active = true;
    let loaded = {
      skills: false,
      departments: false,
      jobTypes: false,
      locations: false,
      clients: false,
    };

    // 🔹 Fetch Skills
    fetchSkills("").then((skills) => {
      if (active && Array.isArray(skills)) {
        setSkillOptions(skills.map((s) => ({ value: s, label: s })));
        loaded.skills = true;
        checkLoaded();
      }
    });

    // 🔹 Fetch Departments
    getAllDepartments().then((res) => {
      if (active) {
        if (res?.status === "S" && Array.isArray(res.result_info)) {
          setDepartmentOptions(
            res.result_info.map((dept) => ({
              value: String(dept.id),
              label: dept.name,
            }))
          );
        } else {
          setDepartmentOptions([]);
        }
        loaded.departments = true;
        checkLoaded();
      }
    });

    // 🔹 Fetch Job Types
    getAllJobTypes().then((res) => {
      if (active) {
        if (res?.status === "S" && Array.isArray(res.result_info)) {
          setJobTypeOptions(
            res.result_info.map((jobType) => ({
              value: String(jobType.id),
              label: jobType.name,
            }))
          );
        } else {
          setJobTypeOptions([]);
        }
        loaded.jobTypes = true;
        checkLoaded();
      }
    });

    // 🔹 Fetch Indian Cities
    const indianCities = getIndianCities();
    if (active) {
      setLocationOptions(indianCities);
      loaded.locations = true;
      checkLoaded();
    }

    // 🔹 Fetch Active Clients
    getActiveClients().then((res) => {
      if (active) {
        if (res?.status === "S" && Array.isArray(res.result_info)) {
          setClientOptions(
            res.result_info.map((client) => ({
              value: String(client.id),
              label: client.name,
              website: client.website,
            }))
          );
        } else {
          setClientOptions([]);
        }
        loaded.clients = true;
        checkLoaded();
      }
    });

    function checkLoaded() {
      if (Object.values(loaded).every(Boolean)) {
        setOptionsLoaded(true);
      }
    }

    return () => {
      active = false;
    };
  }, []);

  const roleId = localStorage.getItem("role_id");
  console.log(roleId);

  if (!optionsLoaded) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        Loading requisition data...
      </div>
    );
  }

  return (
    <div className="create-requisition-container">
      <h2 style={{ fontWeight: 600, fontSize: "1.4rem" }}>Requisition Info</h2>
      {/* Basic Information */}
      {roleId !== "4" && (
        <>
          <div className="create-requisition-section">
            <div className="create-requisition-section-title">
              <span role="img" aria-label="info">
                🔵
              </span>{" "}
              Basic Information
            </div>
            <div className="create-requisition-row">
              {/* <div className="create-requisition-field">
                <label>Job ID </label>
                <input
                  name="job_id"
                  value={form.job_id || ""}
                   onChange={(e) => {
               const cleanValue = DOMPurify.sanitize(e.target.value);

                 setForm((prev) => ({
                     ...prev,
                  job_id: cleanValue,
                   }));
                    }}
                  placeholder="Enter Job ID"
                  required
                  className="custom-placeholder"
                  autoComplete="off"
                  disabled
                />
              </div> */}
              <div className="create-requisition-field">
                  <label>Job Title</label>
                      <input
                          name="job_title"
                          value={form.job_title}
                          onChange={(e) => {
                            const value = e.target.value;

     
                            const hasScript = /<script.*?>/i.test(value);

                            if (!hasScript) {
                                    setForm((prev) => ({
                                     ...prev,
                                     job_title: value,
                                               }));
                                           }
                                        }}
                                 disabled
                                 placeholder="Enter job title"
                                 required
                                 className="custom-placeholder"
                                 autoComplete="off"
                                       />
                                 </div>
              
             </div>
            <div className="create-requisition-row">
              <div className="create-requisition-field">
                <label>Department </label>
                <Select
                  // isMulti
                  options={departmentOptions}
                  value={departmentOptions.filter((opt) =>
                    form.departments.includes(opt.value)
                  )}
                  onChange={handleDepartmentChange}
                  placeholder="Select department(s)"
                  isSearchable={true}
                  isDisabled
                  classNamePrefix="react-select"
                  styles={{
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base) => ({
                      ...base,
                      padding: "0.2rem 0rem",
                    }),
                  }}
                />
              </div>
              {assignedRecruiters?.length > 0 && (
                <div className="create-requisition-field">
                  <label>Recruiters </label>
                  <div
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      backgroundColor: "#f9fafb",
                      minHeight: "38px",
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "4px",
                    }}
                  >
                    {assignedRecruiters.map((recruiter, index) => (
                      <span
                        key={recruiter.id || index}
                        style={{
                          background: "#e5e7eb",
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "0.875rem",
                          color: "#374151",
                        }}
                      >
                        {recruiter.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="create-requisition-row">
              <div className="create-requisition-field">
                <label>Openings</label>
                <input
                  name="openings"
                  type="text"
                  value={form.openings}
                  disabled
                  autoComplete="off"
                  placeholder="Openings"
                  className="custom-placeholder"
                />
              </div>
              <div className="create-requisition-field">
                <label>Experience Level (Years) </label>
                {/* <Select
                options={experienceOptions}
                value={
                  experienceOptions.find(
                    (opt) => opt.value === Number(form.years_of_exp)
                  ) || null
                }
                onChange={(selected) =>
                  setForm((f) => ({
                    ...f,
                    years_of_exp: selected ? selected.value : "",
                  }))
                }
                placeholder="Select experience level"
                isSearchable={true}
                classNamePrefix="react-select"
                styles={{
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                  control: (base) => ({
                    ...base,
                    padding: "0.2rem 0rem",
                  }),
                }}
                isClearable
                isDisabled
              /> */}
                <input
                  name="experience"
                  type="text"
                  min="0"
                  value={form.years_of_exp}
                  onChange={(ev) => {
                    const value = ev.target.value;
                    const digitOnlyPattern = /^\d*$/; // Allow any number of digits
                    if (digitOnlyPattern.test(value)) {
                      setForm({ ...form, years_of_exp: value });
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                      e.preventDefault();
                    }
                  }}
                  // maxLength={255}
                  disabled
                  autoComplete="off"
                  placeholder="Enter years of experience"
                  className="custom-placeholder"
                />
              </div>
            </div>
            {/* <div className="create-requisition-row">
            Only show for Admin (1) and Hiring Manager (3) 
            {(localStorage.getItem("role_id") === "1" ||
              localStorage.getItem("role_id") === "3") && (
              <div className="create-requisition-field">
                <label>Status *</label>
                <Select
                  options={statusOptions}
                  value={statusOptions.find((opt) => opt.value === form.status) || null}
                  onChange={(selected) =>
                    setForm((f) => ({
                      ...f,
                      status: selected ? selected.value : "active",
                    }))
                  }
                  placeholder="Select status"
                  isSearchable={true}
                  classNamePrefix="react-select"
                  styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
                />
              </div>
            )}
          </div>  */}
          </div>
          {/* Job Details */}
          <div className="create-requisition-section">
            <div className="create-requisition-section-title">
              <span role="img" aria-label="details">
                🟦
              </span>{" "}
              Job Details
            </div>
            <div className="create-requisition-row">
              <div className="create-requisition-field" style={{ flex: 2 }}>
                <label>Job Description </label>
                <textarea
                  name="job_desc"
                  value={form.job_desc}
                
                   onChange={(e) => {
               const value = e.target.value;
               const hasScript = /<script.*?>/i.test(value);

                                if (!hasScript) {
                                          setForm((prev) => ({
                                          ...prev,
                                          job_desc: value,
                                                }));
                                            }

                                        }}
                  disabled
                  placeholder="Provide a detailed description of the role, responsibilities, and requirements..."
                  rows="4"
                  className="custom-placeholder"
                />
              </div>
            </div>
            <div className="create-requisition-row">
              <div className="create-requisition-field" style={{ flex: 2 }}>
                <label>Skills </label>
                <Select
                  isMulti
                  options={skillOptions}
                  value={skillOptions.filter((opt) =>
                    form.skills.includes(opt.value)
                  )}
                  onChange={handleSkillsChange}
                  placeholder="Select or type skills"
                  isSearchable={true}
                  classNamePrefix="react-select"
                  styles={{
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base) => ({
                      ...base,
                      padding: "0.2rem 0rem",
                    }),
                  }}
                  isDisabled
                />
              </div>
            </div>
            <div className="create-requisition-row">
              <div className="create-requisition-field">
                <label>Job Type </label>
                <Select
                  // isMulti
                  options={jobTypeOptions}
                  value={jobTypeOptions.filter((opt) =>
                    form.job_type.includes(opt.value)
                  )}
                  onChange={handleJobTypeChange}
                  placeholder="Select job type"
                  isSearchable={true}
                  classNamePrefix="react-select"
                  styles={{
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base) => ({
                      ...base,
                      padding: "0.2rem 0rem",
                    }),
                  }}
                  isDisabled
                />
              </div>
               <div className="create-requisition-field">
                 <label>Work Mode</label>
                 <div className="selected-value-display" aria-readonly>
               
                   <span>{form.workmode ? form.workmode.trim() : "Not specified"}</span>
                 </div>
               </div>
               </div>
               <div className="create-requisition-row">
              <div className="create-requisition-field">
                <label>Location </label>
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  options={locationOptions}
                  value={locationOptions.filter((opt) =>
                  form.locations.includes(opt.value)
                  )}
                  onChange={handleLocationsChange}
                  placeholder="Select location"
                  isSearchable={true}
                  classNamePrefix="react-select"
                  isDisabled
                  styles={{
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                  control: (base) => ({
                  ...base,
                  padding: "0.2rem 0rem",
                   }),
                  }}
                  />
              </div>
            </div>
            
            <div className="create-requisition-row">
              <div className="create-requisition-field">
                <label>Min Budget (in lakhs)</label>
                <input
                  type="text"
                  name="min_budget"
                  value={form.min_budget}
                  onChange={(ev) => {
                    const value = ev.target.value;
                    const isValidNumber = /^\d*$/.test(value);

                    if (isValidNumber) {
                      setForm({ ...form, min_budget: value });
                    }
                  }}
                  placeholder="Enter minimum budget"
                  required
                  min="0"
                  step="0.01"
                  autoComplete="off"
                  disabled
                />
              </div>
              <div className="create-requisition-field">
                <label>Max Budget (in lakhs)</label>
                <input
                  type="number"
                  name="text"
                  value={form.max_budget}
                  onChange={(ev) => {
                    const value = ev.target.value;
                    const isValidNumber = /^\d*$/.test(value);

                    if (isValidNumber) {
                      setForm({ ...form, max_budget: value });
                    }
                  }}
                  placeholder="Enter maximum budget"
                  required
                  min="0"
                  step="0.01"
                  autoComplete="off"
                  disabled
                />
              </div>
            </div>
            <div className="create-requisition-row">
              <div className="create-requisition-field">
                <label>Notice Period in Days </label>
                <input
                  type="text"
                  name="notice_period_days"
                  value={form.notice_period_days}
                  onChange={(ev) => {
                    const value = ev.target.value;

                    // Only allow numeric values greater than or equal to 0
                    const isValidNumber = /^\d*$/.test(value);

                    if (isValidNumber) {
                      setForm({ ...form, notice_period_days: value });
                    }
                  }}
                  min={1}
                  placeholder="Enter notice period in days"
                  required
                  autoComplete="off"
                  disabled
                />
              </div>
            </div>
          </div>
          {/* Additional Information */}
          <div className="create-requisition-section">
            <div className="create-requisition-section-title">
              <span role="img" aria-label="status">
                🟦
              </span>{" "}
              Additional Information
            </div>
            <div className="create-requisition-row">
              <div className="create-requisition-field">
                <label>Client Name </label>
                <Select
                  options={clientOptions}
                  value={
                    clientOptions.find((opt) => opt.value === form.client_id) ||
                    null
                  }
                  placeholder="Select client name"
                  isSearchable={true}
                  classNamePrefix="react-select"
                  styles={{
                    menu: (base) => ({ ...base, zIndex: 9999 }),
                    control: (base) => ({
                      ...base,
                      padding: "0.2rem 0rem",
                    }),
                  }}
                  isClearable
                  isDisabled
                />
              </div>
              <div className="create-requisition-field">
                <label>Client Website </label>
                <input
                  type="text"
                  name="client_website"
                  value={form.client_website}
                  onChange={(ev) => {
                    const value = ev.target.value;
                    const invalidPattern =
                      /<script.*?>.*?<\/script>|[<>\\{}=:/""'']/i;
                    if (!invalidPattern.test(value)) {
                      setForm({ ...form, client_website: value });
                    }
                  }}
                  placeholder="Client website will be auto-populated"
                  required
                  className="custom-placeholder"
                  autoComplete="off"
                  disabled
                />
              </div>
            </div>

            <div className="create-requisition-row">
              <div className="create-requisition-field" style={{ flex: 2 }}>
                <label>Remarks</label>
                <textarea
                  name="remarks"
                  value={form.remarks}
                  onChange={(ev) => {
                    const value = ev.target.value;
                    const invalidPattern =
                      /<script.*?>.*?<\/script>|[<>\\{}=:/""'']/i;
                    if (!invalidPattern.test(value)) {
                      setForm({ ...form, remarks: value });
                    }
                  }}
                  placeholder="Additional notes or remarks..."
                  rows="3"
                  className="custom-placeholder"
                  autoComplete="off"
                  disabled
                />
              </div>
            </div>
          </div>

          {error && (
            <div style={{ color: "red", marginBottom: "1rem" }}>{error}</div>
          )}
          <div className="create-requisition-actions">
            <button
              type="button"
              onClick={() => navigate("/requisitions")}
              className="save-btn"
            >
              Go Back
            </button>
          </div>
        </>
      )}
      {roleId === "4" && (
        <>
          <div className="create-requisition-section">
            <div className="overflow-auto">
              <table
                border="1"
                className="table table-bordered recruiter-table"
              >
                <tr>
                  <th>Job Title</th>
                  <td>{form.job_title}</td>
                </tr>
                <tr>
                  <th>Experience Level</th>
                  <td>{form.years_of_exp} years</td>
                </tr>
                {/* <tr>
                  <th>No of Openings</th>
                  <td>{form.openings}</td>
                </tr> */}
                <tr>
                  <th>No of Openings</th>
                  <td>{form.openings}</td>
                </tr>
                <tr>
                  <th>Notice Period in Days</th>
                  <td>{form.notice_period_days}</td>
                </tr>
                <tr>
                  <th>Min Budget (in lakhs)</th>
                  <td>{form.min_budget}</td>
                </tr>
                <tr>
                  <th>Max Budget (in lakhs)</th>
                  <td>{form.max_budget}</td>
                </tr>
                <tr>
                  <th>Workmode</th>
                  <td>
                    {form.workmode ? form.workmode.trim() : "Not specified"}
                  </td>
                </tr>
                <tr>
                  <th>Job Type</th>
                  <td>
                    {form.job_type && Array.isArray(form.job_type)
                      ? form.job_type
                          .map((typeId) => {
                            const jobType = jobTypeOptions.find(
                              (opt) => opt.value === typeId
                            );
                            return jobType ? jobType.label : typeId;
                          })
                          .join(", ")
                      : form.job_type}
                  </td>
                </tr> 
                  <tr>
                    <th>Skills</th>
                    <td>
                      {form.skills && Array.isArray(form.skills) 
                      ? form.skills.join(", ")
                      : form.skills} </td>
                  </tr>
                  <tr>
                    <th>Client Name</th>
                      <td>{form.client_name}</td>
                  </tr>
                  <tr>
                  <th>Client Website</th>
                    <td>{form.client_website}</td>
                </tr>
                <tr>
                <th>Location</th>
                <td>
                    {Array.isArray(form.locations)
                    ? form.locations.join(", ")
                    : form.locations}
                </td>
                </tr>
                <tr>
                  <th>Remarks</th>
                  <td>{form.remarks ? form.remarks : "-"}</td>
                </tr>
              </table>
            </div>
            <div className="job-requisition-row mt-4">
              <h2 style={{ fontWeight: 600, fontSize: "1.4rem" }}>
                Job Description
              </h2>
              <div className="col-12">{form.job_desc}</div>
              {/* <div className="col-12">
                <label><strong>Job Title:</strong> {form.job_title} </label>
              </div> */}
              {/* <div className="col-12">
                <label><strong>Job ID:</strong> {form.job_id} </label>
              </div> */}
              {/* <div className="col-12">
                <label><strong>Department:</strong> {form.departments && Array.isArray(form.departments) 
                  ? form.departments.map(deptId => {
                      const dept = departmentOptions.find(opt => opt.value === deptId);
                      return dept ? dept.label : deptId;
                    }).join(", ")
                  : form.departments} </label>
              </div> */}
              {/* <div className="col-12">
                <label><strong>Recruiter:</strong> {assignedRecruiters && Array.isArray(assignedRecruiters) 
                  ? assignedRecruiters.map(recruiter => recruiter.name).join(", ")
                  : "No recruiters assigned"} </label>
              </div> */}
              {/* <div className="col-12">
                <label><strong>Experience Level:</strong> {form.years_of_exp} years </label>
              </div>
              <div className="col-12">
                <label><strong>Notice Period in Days:</strong> {form.notice_period_days} </label>
              </div>
              <div className="col-12">
                <label><strong>Min Budget:</strong> {form.min_budget} </label>
              </div>
              <div className="col-12">
                <label><strong>Max Budget:</strong> {form.max_budget} </label>
              </div>
              <div className="col-12">
                <label><strong>Job Type:</strong> {form.job_type && Array.isArray(form.job_type) 
                  ? form.job_type.map(typeId => {
                      const jobType = jobTypeOptions.find(opt => opt.value === typeId);
                      return jobType ? jobType.label : typeId;
                    }).join(", ")
                  : form.job_type} </label>
              </div>
               <div className="col-12">
                <label><strong>Job Description:</strong> {form.job_desc} </label>
              </div> 
              
              
              <div className="col-12">
                <label><strong>Location:</strong> {form.locations} </label>
              </div>
              
              
              <div className="col-12">
                <label><strong>Client Name:</strong> {form.client_name} </label>
              </div>
              <div className="col-12">
                <label><strong>Client Website:</strong> {form.client_website} </label>
              </div>
              <div className="col-12">
                <label><strong>Remarks:</strong> {form.remarks ? form.remarks : "-"} </label>
              </div>
              <div className="border-bottom py-3"></div>
            <div className="job-requisition-row mt-4">
              <h2 style={{ fontWeight: 600, fontSize: "1.4rem" }}>Job Description</h2>
              <div className="col-12">
                {form.job_desc}
              </div>
            </div>*/}
            </div>

            <div className="create-requisition-actions">
              <button
                type="button"
                onClick={() => navigate("/requisitions")}
                className="save-btn"
              >
                Go Back
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default EditRequisition;
